import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Play, Trophy, RefreshCcw, Gamepad2, ArrowLeft } from 'lucide-react';
import { useMusicStore } from '../../store/musicStore';
import { useSfx } from '../../store/sfxStore';

interface Sprite {
    id: number;
    x: number;
    y: number;
    width: number;
    height: number;
    vx: number;
    vy: number;
    emoji: string;
    type: 'player' | 'coin' | 'enemy' | 'powerup';
}

export const BumpAndBounce = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(0);
    const [lives, setLives] = useState(3);
    const [gameOver, setGameOver] = useState(false);
    const [level, setLevel] = useState(1);
    const [invincible, setInvincible] = useState(false);
    const animationRef = useRef<number>();
    const keysRef = useRef<Set<string>>(new Set());

    const { playTrack } = useMusicStore();
    const { play } = useSfx();

    // Game state refs (to avoid stale closures in requestAnimationFrame)
    const stateRef = useRef({
        player: { x: 400, y: 300, width: 40, height: 40, vx: 0, vy: 0, emoji: '🦍', type: 'player' as const },
        coins: [] as Sprite[],
        enemies: [] as Sprite[],
        powerups: [] as Sprite[],
        score: 0,
        lives: 3,
        level: 1,
        invincibleTimer: 0,
        frameCount: 0
    });

    useEffect(() => {
        const saved = localStorage.getItem('bump-and-bounce-highscore');
        if (saved) setHighScore(parseInt(saved));
        playTrack('arcade'); // Arcade music
    }, []);

    // Collision detection: AABB
    const checkCollision = (a: any, b: any): boolean => {
        return (
            a.x < b.x + b.width &&
            a.x + a.width > b.x &&
            a.y < b.y + b.height &&
            a.y + a.height > b.y
        );
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => keysRef.current.add(e.key);
        const handleKeyUp = (e: KeyboardEvent) => keysRef.current.delete(e.key);
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    const spawnCoin = (canvasWidth: number, canvasHeight: number) => {
        return {
            id: Math.random(),
            x: 50 + Math.random() * (canvasWidth - 100),
            y: 50 + Math.random() * (canvasHeight - 100),
            width: 30, height: 30, vx: 0, vy: 0, emoji: '🍌', type: 'coin' as const
        };
    };

    const spawnEnemy = (level: number, canvasWidth: number, canvasHeight: number) => {
        const speed = 2 + (level * 0.5);
        const spawnEdge = Math.random() > 0.5 ? 0 : canvasWidth - 50;
        return {
            id: Math.random(),
            x: spawnEdge,
            y: Math.random() * canvasHeight,
            width: 35, height: 35,
            vx: (Math.random() > 0.5 ? speed : -speed),
            vy: (Math.random() > 0.5 ? speed : -speed),
            emoji: '🐍', type: 'enemy' as const
        };
    };

    const startGame = () => {
        play('click');
        const c = canvasRef.current;
        if (!c) return;

        stateRef.current = {
            player: { x: c.width / 2, y: c.height / 2, width: 40, height: 40, vx: 0, vy: 0, emoji: '🦍', type: 'player' },
            coins: [spawnCoin(c.width, c.height), spawnCoin(c.width, c.height)],
            enemies: [spawnEnemy(1, c.width, c.height)],
            powerups: [],
            score: 0,
            lives: 3,
            level: 1,
            invincibleTimer: 0,
            frameCount: 0
        };

        setScore(0);
        setLives(3);
        setLevel(1);
        setGameOver(false);
        setIsRunning(true);
        setInvincible(false);
    };

    useEffect(() => {
        if (!isRunning || gameOver || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d')!;

        const gameLoop = () => {
            const state = stateRef.current;
            state.frameCount++;

            // Clear
            ctx.fillStyle = '#f0fdf4';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Move player
            const speed = 6 + (state.level * 0.2); // Player gets slightly faster too
            if (keysRef.current.has('ArrowUp') || keysRef.current.has('w')) state.player.y -= speed;
            if (keysRef.current.has('ArrowDown') || keysRef.current.has('s')) state.player.y += speed;
            if (keysRef.current.has('ArrowLeft') || keysRef.current.has('a')) state.player.x -= speed;
            if (keysRef.current.has('ArrowRight') || keysRef.current.has('d')) state.player.x += speed;

            state.player.x = Math.max(0, Math.min(canvas.width - state.player.width, state.player.x));
            state.player.y = Math.max(0, Math.min(canvas.height - state.player.height, state.player.y));

            // Invincibility
            if (state.invincibleTimer > 0) {
                state.invincibleTimer--;
                if (state.invincibleTimer === 0) setInvincible(false);
            }

            // Powerup Spawn Logic (Rare)
            if (state.frameCount % 500 === 0 && Math.random() > 0.5) {
                state.powerups.push({
                    id: Math.random(),
                    x: Math.random() * (canvas.width - 40),
                    y: Math.random() * (canvas.height - 40),
                    width: 35, height: 35, vx: 0, vy: 0, emoji: '⭐', type: 'powerup'
                });
            }

            // Move enemies
            state.enemies.forEach(enemy => {
                enemy.x += enemy.vx;
                enemy.y += enemy.vy;
                if (enemy.x < 0 || enemy.x > canvas.width - enemy.width) enemy.vx *= -1;
                if (enemy.y < 0 || enemy.y > canvas.height - enemy.height) enemy.vy *= -1;
            });

            // Collisions - Coins
            let collected = 0;
            state.coins = state.coins.filter(coin => {
                if (checkCollision(state.player, coin)) {
                    collected++;
                    play('success');
                    return false; // remove coin
                }
                return true;
            });

            if (collected > 0) {
                state.score += collected * 10;
                setScore(state.score);
                // Level up every 50 points
                const newLevel = Math.floor(state.score / 50) + 1;
                if (newLevel > state.level) {
                    state.level = newLevel;
                    setLevel(newLevel);
                    play('success');
                    // Add new enemy
                    state.enemies.push(spawnEnemy(state.level, canvas.width, canvas.height));
                }
                // Replenish coins
                while (state.coins.length < 2 + Math.floor(state.level / 2)) {
                    state.coins.push(spawnCoin(canvas.width, canvas.height));
                }
            }

            // Collisions - Powerups
            state.powerups = state.powerups.filter(p => {
                if (checkCollision(state.player, p)) {
                    play('success');
                    state.invincibleTimer = 180; // ~3 seconds at 60fps
                    setInvincible(true);
                    state.score += 25;
                    setScore(state.score);
                    return false;
                }
                return true; // disappear after 500 frames maybe? We keep it simple
            });

            // Collisions - Enemies
            if (state.invincibleTimer === 0) {
                for (let enemy of state.enemies) {
                    if (checkCollision(state.player, enemy)) {
                        play('error');
                        state.lives -= 1;
                        setLives(state.lives);
                        state.invincibleTimer = 60; // 1 sec iframes after hit
                        setInvincible(true);
                        
                        if (state.lives <= 0) {
                            setGameOver(true);
                            setIsRunning(false);
                            if (state.score > highScore) {
                                localStorage.setItem('bump-and-bounce-highscore', state.score.toString());
                                setHighScore(state.score);
                            }
                        }
                        break;
                    }
                }
            }

            // Draw game
            ctx.font = '30px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // Draw Powerups
            state.powerups.forEach(p => ctx.fillText(p.emoji, p.x + p.width / 2, p.y + p.height / 2));

            // Draw Coins
            state.coins.forEach(c => ctx.fillText(c.emoji, c.x + c.width / 2, c.y + c.height / 2));
            
            // Draw Enemies
            state.enemies.forEach(e => ctx.fillText(e.emoji, e.x + e.width / 2, e.y + e.height / 2));

            // Draw Player (blink if invincible)
            if (state.invincibleTimer === 0 || Math.floor(state.invincibleTimer / 5) % 2 === 0) {
                ctx.font = '35px Arial';
                ctx.fillText(
                    state.invincibleTimer > 60 ? '🤩' : state.player.emoji, 
                    state.player.x + state.player.width / 2, 
                    state.player.y + state.player.height / 2
                );
            }

            if (!gameOver) {
                animationRef.current = requestAnimationFrame(gameLoop);
            }
        };

        animationRef.current = requestAnimationFrame(gameLoop);
        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, [isRunning, gameOver, highScore]);

    return (
        <div className="min-h-screen bg-slate-900 text-white font-sans overflow-hidden">
            {/* Arcade Cabinet Header */}
            <div className="bg-slate-800 border-b-4 border-green-500 p-4">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <Link to="/games" className="btn bg-slate-700 text-white hover:bg-slate-600 flex items-center gap-2 group px-4 py-2 rounded-lg font-bold">
                        <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" /> Arcade
                    </Link>
                    <div className="flex items-center gap-3">
                        <Gamepad2 className="text-green-400" size={32} />
                        <h1 className="text-3xl font-black font-comic tracking-wider text-green-400">
                            BUMP & BOUNCE
                        </h1>
                    </div>
                    <div className="bg-slate-900 border-2 border-slate-700 px-4 py-2 rounded-xl flex items-center gap-3">
                        <Trophy className="text-yellow-500" size={20} />
                        <div className="font-mono font-bold">
                            <span className="text-slate-400 text-xs block leading-none">HIGH SCORE</span>
                            <span className="text-yellow-500 text-xl leading-none">{highScore}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto p-6 md:p-12 relative flex justify-center">
                {/* Arcade Machine Bezel */}
                <div className="bg-slate-800 p-6 rounded-[2rem] shadow-[0_0_50px_rgba(34,197,94,0.2)] border-8 border-slate-700 relative">
                    
                    {/* Screen Glass effect */}
                    <div className="absolute inset-4 rounded-[1.5rem] pointer-events-none shadow-[inset_0_0_20px_rgba(0,0,0,0.5)] z-10"></div>
                    
                    {/* HUD Layer built into cabinet */}
                    <div className="flex justify-between items-center bg-slate-900 p-3 rounded-t-2xl border-b-2 border-slate-800">
                        <div className="text-green-400 font-mono font-bold text-xl tracking-widest pl-4">
                            SCORE:{score.toString().padStart(5, '0')}
                        </div>
                        <div className="text-slate-400 font-mono font-bold tracking-widest">
                            LEVEL:{level}
                        </div>
                        <div className="flex gap-1 pr-4">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className={`w-6 h-6 rounded-full border-2 border-red-500 ${i < lives ? 'bg-red-500' : 'bg-transparent'}`} />
                            ))}
                        </div>
                    </div>

                    {/* Canvas Target */}
                    <div className="relative bg-slate-900 rounded-b-2xl overflow-hidden">
                        <canvas
                            ref={canvasRef}
                            width={800}
                            height={500}
                            className={`w-full max-w-[800px] bg-[#f0fdf4] transition-all cursor-crosshair ${invincible ? 'shadow-[inset_0_0_50px_rgba(234,179,8,0.3)]' : ''}`}
                            style={{ imageRendering: 'pixelated' }}
                        />

                        {/* Overlays */}
                        <AnimatePresence>
                            {!isRunning && !gameOver && (
                                <motion.div 
                                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                    className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm flex flex-col items-center justify-center z-20"
                                >
                                    <div className="bg-green-500 text-white px-6 py-2 rounded-full font-black tracking-widest mb-8 animate-pulse text-xl shadow-[0_0_20px_rgba(34,197,94,0.5)]">
                                        INSERT COIN
                                    </div>
                                    <div className="text-slate-300 font-mono text-center mb-8 space-y-2">
                                        <p>⬅️⬆️⬇️➡️ to Move</p>
                                        <p>🍌 Collect Bananas</p>
                                        <p>🐍 Avoid Snakes</p>
                                        <p>⭐ Grab Stars</p>
                                    </div>
                                    <button
                                        onClick={startGame}
                                        className="bg-white text-green-600 px-10 py-4 rounded-xl font-black text-2xl hover:scale-110 active:scale-95 transition-all shadow-[0_10px_0_#16a34a] hover:shadow-[0_5px_0_#16a34a] hover:translate-y-1"
                                    >
                                        START GAME
                                    </button>
                                </motion.div>
                            )}

                            {gameOver && (
                                <motion.div 
                                    initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                                    className="absolute inset-0 bg-red-900/90 backdrop-blur-md flex flex-col items-center justify-center z-20 text-center"
                                >
                                    <h2 className="text-6xl font-black font-comic text-red-500 mb-2 drop-shadow-[0_0_15px_rgba(239,68,68,0.5)]">
                                        GAME OVER
                                    </h2>
                                    <p className="text-2xl text-white font-mono mb-8">FINAL SCORE: {score}</p>
                                    
                                    <div className="flex gap-4">
                                        <button
                                            onClick={startGame}
                                            className="bg-red-500 text-white px-8 py-4 rounded-xl font-black hover:bg-red-400 flex items-center gap-2 shadow-[0_6px_0_#991b1b] active:shadow-[0_0px_0_#991b1b] active:translate-y-2 transition-all"
                                        >
                                            <RefreshCcw /> PLAY AGAIN
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </div>
    );
};
